import React, { useCallback } from 'react';

import { WarningMessages } from 'constants/Warning.constants';
import { PATHS } from 'routes/routes.constants';

import { useRouter } from '@polyai/common/hooks/useRouter';
import { selectAllTranslations } from '@polyai/stream/store/reducers/translations/selectors';
import { GlobeIcon } from '@polyai/ui/components/atoms/Icons';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';

import { TRANSLATION_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import { useAppSelector } from 'hooks/reduxHooks';
import useProjectRoute from 'hooks/useProjectRoute';
import { generateNextPath } from 'lib/routeHelper';
import { ResponseControl } from 'screens/ResponseControl/types';
import { useViewMode } from 'screens/withPermissions/useViewMode';
import { TRANSLATION_ACTION_COPY } from './Translation.uiCopy';

const TranslationTagContent = ({
  id,
  remove,
  closePopup,
}: ActionTagContentProps) => {
  const { pushUrl } = useRouter();
  const { accountId, projectId } = useProjectRoute();
  const { isEditable } = useViewMode();

  const handleViewTranslation = useCallback(() => {
    const path = generateNextPath(
      PATHS.RESPONSE_CONTROL,
      {
        accountId,
        projectId,
        type: ResponseControl.TRANSLATIONS,
      },
      { translationId: id },
    );
    pushUrl(path);
    closePopup?.();
  }, [accountId, projectId, pushUrl, id, closePopup]);

  return (
    <React.Fragment>
      <PopupMenuItem
        data-test-id="view-translation"
        onClick={handleViewTranslation}
      >
        {isEditable
          ? TRANSLATION_ACTION_COPY.edit
          : TRANSLATION_ACTION_COPY.view}
      </PopupMenuItem>
      {isEditable && (
        <PopupMenuItem isDelete onClick={remove}>
          {TRANSLATION_ACTION_COPY.remove}
        </PopupMenuItem>
      )}
    </React.Fragment>
  );
};

export const useTranslationActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { isTranslationsEnabled } = useActionsContext();
  const translations = useAppSelector(selectAllTranslations);
  const translation = translations?.find(t => t.id === id);

  const warning = !translation
    ? WarningMessages.TRANSLATION_NOT_EXIST
    : undefined;

  return {
    Content: TranslationTagContent,
    Icon: GlobeIcon,
    name: translation?.translationKey ?? id,
    warning,
    prefix: TRANSLATION_PREFIX,
    isEnabled: isTranslationsEnabled,
    isLoading: false,
  };
};
